// ZmenyBotuKarta — Můj účet → Změny od botů (FW-BOTI etapa 4, 22. 10. 2026).
//
// Co AI boti týmu změnili v úkolech a meetingách, se stavem před a po. Člověk vidí jen
// svého bota za posledních 24 hodin a může jeho změnu vrátit. Owner vidí všechny boty
// za 180 dní (filtr člověk, sekce, období) a vrací kteroukoli změnu. Smazaný úkol jde
// 180 dní obnovit. Vrátit jde jen změna, se kterou od té doby nikdo nepracoval.
// Karta se ukáže jen ownerovi a lidem, kteří mají vydaný token bota.
import React, { useMemo, useState } from "react";
import { History, Bot, RotateCcw, ChevronDown } from "lucide-react";
import Card from "./Card.jsx";
import { ErrorNote, SkeletonRows, Stitek, RozbalovaciVyber } from "./ui.jsx";
import { useApiData, apiFetch } from "../lib/api.js";
import { toast } from "./Toast.jsx";
import { ton } from "../lib/tony.js";
import { getCurrentUser } from "../lib/auth.js";
import { confirmDialog } from "./confirmHost.jsx";

const SEKCE = { ukoly: "Úkoly", meetingy: "Meetingy", rutiny: "Rutiny", fakturace: "Fakturace", absence: "Absence", upominky: "Upomínky", skoleni: "Školení", bonusy: "Bonusy a srážky", vyplaty: "Výplaty", ukoly_trenery: "Úkoly trenérů", hlaseni: "Hlášení", cile_mesice: "Cíle měsíce", fond_hodin: "Fond hodin", cashflow_model: "Model cashflow" };
const POLE = {
  nazev: "název", popis: "popis", kategorie: "kategorie", pilot: "pilot", zadavatel: "zadavatel", deadline: "termín",
  priority: "priorita", tags: "štítky", status: "stav", hotovo_kdy: "hotovo", meeting_id: "meeting", kontrola: "kontrola",
  odlozeno: "odloženo", parent_id: "nadřazený úkol", je_projekt: "projekt", podukoly: "podúkoly",
  agenda: "agenda", sekce: "zápis", poznamky: "poznámky", datum: "datum", typ: "typ", stav: "stav",
  frekvence: "frekvence", checklist: "checklist", deadline_den: "den termínu", zadat_od_dne: "zadat od dne", aktivni: "aktivní",
  plati_od: "platí od", plati_do: "platí do",
  person_display: "osoba", osoba: "osoba", poradi_v_mesici: "pořadí v měsíci", je_pokuta: "pokuta", castka_pokuty: "částka pokuty", datum_upominky: "datum upomínky", mesic: "měsíc", person_id: "osoba (id)", poznamka: "poznámka", person_role: "role", date_from: "od", date_to: "do", time_from: "od hodiny", time_to: "do hodiny", duvod: "důvod",
  approved_by: "schválil", approved_at: "schváleno", rejected_by: "zamítl", rejected_at: "zamítnuto", rejected_reason: "důvod zamítnutí",
  dalsi: "příští faktura", konec: "konec", cena: "cena", klientka: "klientka", balicek: "balíček", prvni: "první faktura", hned: "hned",
  zalozeni_id: "číslo založení", faktura: "faktura", chyba: "chyba",
  castka: "částka", datum_vzniku: "datum vzniku", duvod_storna: "důvod storna", mesic_vyplaceni: "měsíc vyplacení", klient_name: "klient", sablona_kod: "šablona", navrhl_by: "navrhl", schvalil_by: "schválil", schvalil_at: "schváleno", vyplacen_by: "vyplatil", vyplacen_at: "vyplaceno",
  dpp_polozky: "DPP položky", dpp_castka: "DPP částka", dpp_hodiny: "DPP hodiny", dpp_bonus_castka: "DPP bonus", hodiny_rucni: "ruční hodiny", odecty: "jednorázové odečty", odecty_override: "odečty srovnané ručně", zaklad_rucni: "ruční základ", zaklad_rucni_duvod: "důvod základu", schvaleno: "schváleno", faktura_prijata: "faktura přijata",
  text: "text", bonusy: "bonusy", metric_type: "metrika", metric_target: "cíl metriky", metric_trener: "trenér metriky (id)", poradi: "pořadí", zdroj: "zdroj",
  splneno: "splněno", nesplneno: "nesplněno", splneno_at: "splněno kdy", hr_propsano: "propsáno do HR",
  obdobi: "měsíc", hodin: "hodin", zadal: "zadal",
  zkontroloval: "zkontroloval(a)", zkontrolovano_at: "zkontrolováno",
  person_name: "osoba", skoleni_id: "školení (id)", prirazeno_at: "přiřazeno", prirazeno_kym: "přiřadil", started_at: "začátek testu", finished_at: "dokončeno", spravne: "správně", celkem: "otázek", procent: "procent",
};
const JSON_POLE = new Set(["agenda", "sekce", "poznamky", "checklist"]);
// Model cashflow: pole jsou řádky modelu („model:Revenue / Osobní tréninky“), popisek je název řádku.
const popisPole = (pole) => POLE[pole] || (String(pole).startsWith("model:") ? String(pole).slice(6) : null);
const SLOVESO = {
  vytvoreni: { ukoly: "založil úkol", meetingy: "založil meeting", rutiny: "založil rutinu", absence: "založil absenci", upominky: "přidal upomínku", skoleni: "přiřadil test", bonusy: "založil bonus nebo srážku", vyplaty: "zadal úpravy výplaty", ukoly_trenery: "založil úkol trenéra", cile_mesice: "založil cíl měsíce", fond_hodin: "přidal úpravu fondu hodin" },
  uprava: { ukoly: "upravil úkol", meetingy: "upravil meeting", rutiny: "upravil rutinu", fakturace: "změnil fakturaci", absence: "upravil absenci (schválení nebo zamítnutí)", bonusy: "upravil bonus nebo srážku", vyplaty: "upravil výplatu (DPP, hodiny nebo odečty)", ukoly_trenery: "upravil úkol trenéra", hlaseni: "změnil stav hlášení", cile_mesice: "upravil cíl měsíce", cashflow_model: "upravil model cashflow" },
  smazani: { ukoly: "smazal úkol", meetingy: "smazal meeting", rutiny: "smazal rutinu", absence: "smazal absenci", upominky: "smazal upomínku", skoleni: "smazal pokus o test", bonusy: "smazal bonus nebo srážku", ukoly_trenery: "smazal úkol trenéra", cile_mesice: "smazal cíl měsíce", fond_hodin: "smazal úpravu fondu hodin" },
  vraceni: { ukoly: "vrátil(a) změnu bota u úkolu", meetingy: "vrátil(a) změnu bota u meetingu", rutiny: "vrátil(a) změnu bota u rutiny", fakturace: "vrátil(a) změnu bota u fakturace", absence: "vrátil(a) změnu bota u absence", upominky: "vrátil(a) změnu bota u upomínky", skoleni: "vrátil(a) změnu bota u školení", bonusy: "vrátil(a) změnu bota u bonusu nebo srážky", vyplaty: "vrátil(a) změnu bota u výplaty", ukoly_trenery: "vrátil(a) změnu bota u úkolu trenéra", hlaseni: "vrátil(a) změnu bota u hlášení", cile_mesice: "vrátil(a) změnu bota u cíle měsíce", fond_hodin: "vrátil(a) změnu bota u fondu hodin", cashflow_model: "vrátil(a) změnu bota u modelu cashflow" },
  zalozeni: { fakturace: "založil fakturaci" },
  zruseni: { fakturace: "zrušil naplánované založení fakturace" },
  drive: { fakturace: "vystavil fakturu dřív" },
  drive_nechat: { fakturace: "nechal fakturu podle plánu" },
};

const dvoj = (n) => String(n).padStart(2, "0");
function kdy(d) {
  const x = new Date(d); const dnes = new Date();
  const vcera = new Date(); vcera.setDate(dnes.getDate() - 1);
  const cas = `${x.getHours()}:${dvoj(x.getMinutes())}`;
  if (x.toDateString() === dnes.toDateString()) return `dnes ${cas}`;
  if (x.toDateString() === vcera.toDateString()) return `včera ${cas}`;
  return `${x.getDate()}. ${x.getMonth() + 1}. ${cas}`;
}
const datum = (d) => { const x = new Date(d); return `${x.getDate()}. ${x.getMonth() + 1}. ${x.getFullYear()}`; };
/** Hodnota pole pro přehled: datum česky, ano/ne, prázdná hodnota jako „nic“. */
function hodnota(pole, v) {
  if (v === null || v === undefined || v === "") return "nic";
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) { const [r, m, d] = v.slice(0, 10).split("-"); return `${Number(d)}. ${Number(m)}. ${r}`; }
  if (v === "true") return "ano";
  if (v === "false") return "ne";
  if (pole === "hodin") return `${v.replace(".", ",")} h`;
  return v.replace(/^"|"$/g, "");
}

async function vratit(id) {
  const r = await apiFetch("/api/boti-zmeny", { method: "POST", body: JSON.stringify({ akce: "vratit", id }) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export default function ZmenyBotuKarta() {
  const [kdo, setKdo] = useState("");
  const [sekce, setSekce] = useState("");
  const [dni, setDni] = useState("7");
  const url = useMemo(() => {
    const q = new URLSearchParams();
    if (kdo) q.set("email", kdo);
    if (sekce) q.set("sekce", sekce);
    if (dni) q.set("dni", dni);
    return `/api/boti-zmeny?${q.toString()}`;
  }, [kdo, sekce, dni]);
  const { data, loading, error, refetch } = useApiData(url, { bezKese: true });
  const [prace, setPrace] = useState("");

  const jsemOwner = String(getCurrentUser()?.role || "").toLowerCase() === "owner";
  // Kdo bota nemá, kartu vůbec nevidí, ani během načítání.
  if (loading && !data) return jsemOwner ? <Card title="Změny od botů" ikona={History}><SkeletonRows rows={3} /></Card> : null;
  // Chybu ukazujeme jen ownerovi, ostatním by karta bez bota jen překážela.
  if (error && !data) return jsemOwner
    ? <ErrorNote onRetry={refetch}>Změny od botů se nepodařilo načíst.</ErrorNote> : null;
  if (!data || (!data.owner && !data.ma_bota && !(data.zmeny || []).length)) return null;

  const owner = !!data.owner;
  const zmeny = data.zmeny || [];

  const naVraceni = async (z) => {
    const obnova = z.akce === "smazani";
    const ok = await confirmDialog({
      title: obnova ? "Obnovit smazanou položku?" : z.akce === "vytvoreni" ? "Vrátit založení?" : "Vrátit změnu bota?",
      description: obnova
        ? (z.sekce === "ukoly" ? `„${z.nazev}“ se vrátí i s podúkoly a komentáři.` : `„${z.nazev}“ se vrátí zpět.`)
        : z.akce === "vytvoreni"
          ? (z.sekce === "ukoly" ? `„${z.nazev}“ se smaže i s podúkoly, jako by ho bot nezaložil.` : `„${z.nazev}“ se smaže, jako by ho bot nezaložil.`)
          : `„${z.nazev}“ se vrátí do stavu před změnou bota.`,
      confirmLabel: obnova ? "Obnovit" : "Vrátit",
      variant: z.akce === "vytvoreni" ? "danger" : "default",
    });
    if (!ok) return;
    setPrace(z.id);
    try { await vratit(z.id); toast(obnova ? "Obnoveno" : "Vráceno", "success"); refetch(); }
    catch (e) { toast(e.message, "error"); }
    finally { setPrace(""); }
  };

  return (
    <Card title={owner ? "Změny od botů" : "Změny od mého bota"} ikona={History}
      odznak={owner ? "všichni boti, 180 dní" : "posledních 24 hodin"}>
      {owner && (
        <div className="mb-3 grid gap-2 sm:grid-cols-3">
          <RozbalovaciVyber stitek="Kdo" value={kdo} onChange={setKdo} aria-label="Čí bot"
            items={[{ key: "", label: "Všichni" }, ...(data.lidi || []).map((l) => ({ key: l.email, label: l.jmeno || l.email }))]} />
          <RozbalovaciVyber stitek="Sekce" value={sekce} onChange={setSekce} aria-label="Sekce"
            items={[{ key: "", label: "Vše" }, ...Object.entries(SEKCE).map(([key, label]) => ({ key, label }))]} />
          <RozbalovaciVyber stitek="Období" value={dni} onChange={setDni} aria-label="Období"
            items={[{ key: "1", label: "24 hodin" }, { key: "7", label: "7 dní" }, { key: "30", label: "30 dní" }, { key: "180", label: "180 dní" }]} />
        </div>
      )}
      {zmeny.length === 0 && (
        <p className="text-sm italic text-slate-500">{owner ? "V tomhle výběru žádné změny od botů." : "Tvůj bot za posledních 24 hodin nic nezměnil."}</p>
      )}
      <div className="divide-y divide-slate-100">
        {zmeny.map((z) => <Radek key={z.id} z={z} owner={owner} prace={prace} onVratit={naVraceni} />)}
      </div>
      {!owner && <p className="mt-3 text-xs text-slate-500">Starší změny už tady nevidíš. Kdyby bylo potřeba vrátit něco staršího, napiš Láďovi.</p>}
    </Card>
  );
}

function Radek({ z, owner, prace, onVratit }) {
  const [vse, setVse] = useState(false);
  const zmeny = (z.zmeny || []).filter((x) => popisPole(x.pole));
  const ukazat = vse ? zmeny : zmeny.slice(0, 3);
  const vraceni = z.akce === "vraceni";
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Stitek>{kdy(z.kdy)}</Stitek>
          <Stitek ton="znacka">{SEKCE[z.sekce] || z.sekce}</Stitek>
          {owner && <Stitek ton="znacka">{z.jmeno || z.email}</Stitek>}
          {z.nastroj && <Stitek ton="znacka" ikona={Bot}>{z.nastroj}</Stitek>}
          {z.vraceno_at && <Stitek ton="ok">vráceno {datum(z.vraceno_at)}{z.vratil ? `, ${z.vratil}` : ""}</Stitek>}
          {z.akce === "smazani" && !z.vraceno_at && z.do_kdy && <Stitek ton="pozor">obnova do {datum(z.do_kdy)}</Stitek>}
        </div>
        <div className="mt-1.5 text-sm text-slate-800">
          <span className="font-semibold">{vraceni ? (z.vratil || "Člověk") : owner ? "Bot" : "Tvůj bot"}</span> {(SLOVESO[z.akce] || {})[z.sekce] || z.akce} <span className="font-bold">„{z.nazev || "bez názvu"}“</span>
        </div>
        {!vraceni && zmeny.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5 text-[13px]">
            {ukazat.map((x) => (
              <li key={x.pole} className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 break-words rounded-full border border-slate-200 bg-slate-50 px-3 py-1 leading-snug">
                <span className="font-semibold text-slate-500">{popisPole(x.pole)}</span>
                <span className="text-slate-300" aria-hidden="true">|</span>
                {JSON_POLE.has(x.pole)
                  ? <span className="text-slate-700">{z.akce === "vytvoreni" ? "vyplněno" : "upraveno"}</span>
                  : z.akce === "vytvoreni" ? <span className={`font-semibold ${ton("ok").text}`}>{hodnota(x.pole, x.po)}</span>
                  : z.akce === "smazani" ? <span className={`${ton("problem").text} line-through`}>{hodnota(x.pole, x.pred)}</span>
                  : <><span className={`${ton("problem").text} line-through`}>{hodnota(x.pole, x.pred)}</span><span className="text-slate-400">→</span><span className={`font-semibold ${ton("ok").text}`}>{hodnota(x.pole, x.po)}</span></>}
              </li>
            ))}
          </ul>
        )}
        {zmeny.length > 3 && (
          <button type="button" className="btn-ghost btn-sm mt-1" onClick={() => setVse(!vse)} aria-expanded={vse}>
            <ChevronDown className={`h-4 w-4 transition-transform ${vse ? "" : "-rotate-90"}`} /> {vse ? "Méně" : `Další změny (${zmeny.length - 3})`}
          </button>
        )}
      </div>
      {z.lze_vratit && (
        <button type="button" className={`${z.akce === "smazani" ? "btn-primary" : "btn-secondary"} btn-sm shrink-0 self-start`} disabled={!!prace} onClick={() => onVratit(z)}>
          <RotateCcw className="h-4 w-4" /> {z.akce === "smazani" ? "Obnovit" : "Vrátit"}
        </button>
      )}
    </div>
  );
}
